import { Controller } from '@hotwired/stimulus'
import mapboxgl from 'mapbox-gl'

// Map + station list/detail selection for the WMATA dashboard (IMH-style).
// Summary, list, and drawer markup are server-rendered; this controller places
// Mapbox markers, toggles list/detail, and eases the map to the selected station.
export default class extends Controller {
  static targets = [
    'map',
    'drawer',
    'stationTemplates',
    'listPanel',
    'detailPanel',
    'stationList',
    'stationRow'
  ]

  static values = {
    stationsUrl: String,
    payload: Object
  }

  connect () {
    this.activeStationId = null
    this.markers = []
    this.initMap()
    this.startRefresh()
  }

  disconnect () {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
    this.markers.forEach((m) => m.remove())
    if (this.map) this.map.remove()
  }

  initMap () {
    const token = window.mapbox_data?.access_token
    if (!token || !this.hasMapTarget) return

    const coords = window.mapbox_data?.coordinates
    const center = Array.isArray(coords) && coords.length === 2
      ? coords
      : [-77.0369, 38.9072]

    mapboxgl.accessToken = token
    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: 'mapbox://styles/mapbox/streets-v9',
      center,
      zoom: 11
    })
    this.map.addControl(new mapboxgl.NavigationControl(), 'bottom-right')
    this.map.on('load', () => this.renderMarkers(true))
  }

  startRefresh () {
    if (this.refreshTimer || !this.stationsUrlValue) return
    this.refreshTimer = setInterval(() => this.refresh(), 30 * 60 * 1000)
  }

  refresh () {
    fetch(this.stationsUrlValue, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((payload) => {
        this.payloadValue = payload
        this.renderMarkers(false)
        if (this.activeStationId) this.openStationById(this.activeStationId, { focusMap: false })
      })
      .catch(() => { /* keep last known data */ })
  }

  stations () {
    return this.payloadValue?.stations || []
  }

  selectStation (event) {
    const stationId = event.currentTarget.dataset.stationId
    if (!stationId) return
    this.openStationById(stationId, { focusMap: true })
  }

  showList () {
    this.activeStationId = null
    this.highlightRow(null)
    if (this.hasListPanelTarget) this.listPanelTarget.classList.remove('hidden')
    if (this.hasDetailPanelTarget) this.detailPanelTarget.classList.add('hidden')
  }

  renderMarkers (fitMap) {
    if (!this.map) return
    this.markers.forEach((m) => m.remove())
    this.markers = []

    const bounds = new mapboxgl.LngLatBounds()
    this.stations().forEach((station) => {
      if (station.lat == null || station.lng == null) return
      const el = document.createElement('button')
      el.type = 'button'
      el.className = 'wmata-map-marker'
      el.style.cssText = `
        width: 18px; height: 18px; border-radius: 9999px; border: 2px solid #fff;
        box-shadow: 0 1px 4px rgba(0,0,0,.35); cursor: pointer;
        background: ${this.statusColor(station.status)};
      `
      el.title = station.name
      el.addEventListener('click', () => this.openStationById(station.id, { focusMap: true }))

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([station.lng, station.lat])
        .addTo(this.map)
      this.markers.push(marker)
      bounds.extend([station.lng, station.lat])
    })

    if (fitMap && !bounds.isEmpty()) {
      this.map.fitBounds(bounds, { padding: 48, maxZoom: 13 })
    }
  }

  statusColor (status) {
    if (status === 'green') return '#16a34a'
    if (status === 'red') return '#dc2626'
    return '#f59e0b'
  }

  openStationById (stationId, { focusMap = true } = {}) {
    this.activeStationId = stationId
    if (!this.hasDrawerTarget || !this.hasStationTemplatesTarget) return

    const id = String(stationId)
    const template = Array.from(
      this.stationTemplatesTarget.querySelectorAll('template[data-station-id]')
    ).find((node) => node.dataset.stationId === id)
    if (!template) return

    this.drawerTarget.replaceChildren(template.content.cloneNode(true))
    if (this.hasListPanelTarget) this.listPanelTarget.classList.add('hidden')
    if (this.hasDetailPanelTarget) this.detailPanelTarget.classList.remove('hidden')
    this.highlightRow(id)

    if (focusMap) this.focusStationOnMap(id)
  }

  focusStationOnMap (stationId) {
    if (!this.map) return
    const station = this.stations().find((s) => String(s.id) === String(stationId))
    if (!station || station.lat == null || station.lng == null) return

    this.map.easeTo({
      center: [station.lng, station.lat],
      zoom: Math.max(this.map.getZoom(), 13),
      duration: 450
    })
  }

  highlightRow (stationId) {
    if (!this.hasStationRowTarget) return
    this.stationRowTargets.forEach((row) => {
      const selected = stationId != null && row.dataset.stationId === String(stationId)
      row.classList.toggle('bg-gray-50', selected)
      row.classList.toggle('border-gray-300', selected)
    })
  }
}
